import type { ColumnDef, MetaField, MetaEnum } from '@b1links/core';
import type { EntityMeta } from '../api/client';

/** Human label from a Service Layer property name: "DocDueDate" → "Doc Due Date", "U_AppUser" → "App User" */
export function humanize(name: string): string {
  return name.replace(/^U_/, '').replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2').trim();
}

export const labelFor = (field: string, meta?: MetaField, explicit?: string) => explicit ?? meta?.description ?? humanize(field);

/** Enum member name → readable. Strips SAP prefixes: "bost_Open"→"Open", "boposPlanned"→"Planned", "cCustomer"→"Customer", "tYES"→"Yes" */
export function enumLabel(member: string): string {
  // underscore style: drop "prefix_"; camelCase style: drop the leading lowercase run before the first uppercase
  const s = member.includes('_') ? member.replace(/^[a-z]+_/, '') : member.replace(/^[a-z]{1,8}(?=[A-Z])/, '');
  return s === 'YES' ? 'Yes' : s === 'NO' ? 'No' : humanize(s);
}

export function findField(meta: EntityMeta | undefined, field: string, complexType?: string): MetaField | undefined {
  if (!meta) return undefined;
  const list = complexType ? meta.complexTypes[complexType]?.fields : meta.fields;
  return list?.find(f => f.name === field);
}

export function enumFor(meta: EntityMeta | undefined, f?: MetaField): MetaEnum | undefined {
  return f?.enumType && meta ? meta.enums[f.enumType] : undefined;
}

/** URL key of a record: the plain value, or "K1=v1,K2=v2" for composite keys (parsed by the data API). */
export function recordKey(meta: EntityMeta | undefined, rec: Record<string, unknown>): string | undefined {
  const keys = meta?.keys ?? [];
  if (!keys.length || keys.some(k => rec[k] == null)) return undefined;
  return keys.length === 1 ? String(rec[keys[0]]) : keys.map(k => `${k}=${rec[k]}`).join(',');
}

/** Interpolate a {Field} template against a record (computed/derived display value). */
export function interpolate(template: string, rec: Record<string, unknown>): string {
  return template.replace(/\{([\w.]+)\}/g, (_, k) => { const v = rec[k]; return v == null ? '' : String(v); });
}

/** Field names referenced by a {Field} template. */
export function templateFields(template: string): string[] {
  return [...template.matchAll(/\{([\w.]+)\}/g)].map(m => m[1]);
}

export function formatValue(v: unknown, col: ColumnDef, mf?: MetaField, currency?: string): string {
  if (v == null || v === '') return '';
  const fmt = col.format ?? (mf?.kind === 'decimal' ? 'number' : mf?.kind === 'datetime' ? 'date' : mf?.kind === 'enum' ? 'enum' : 'text');
  switch (fmt) {
    case 'money': return `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ' ' + currency : ''}`;
    case 'number': return Number(v).toLocaleString(undefined, { maximumFractionDigits: 6 });
    case 'date': return String(v).slice(0, 10);
    case 'datetime': return String(v).replace('T', ' ').slice(0, 16);
    case 'bool': return v === true || v === 'tYES' ? 'Yes' : 'No';
    case 'enum': case 'status': return enumLabel(String(v));
    default: return String(v);
  }
}
